import { botOpsFetch, BotOpsResponse } from './botOpsClient';
import { ActivityData, AuditRow, Bot, Instruction, InstructionKind, Prefs, SavedView, Scorecard, Snapshot, SnapshotMeta } from '../lib/types';

export interface HealthData {
  enabled: boolean;
  s3_configured: boolean;
  webhook_configured: boolean;
  snapshot: { sha256: string; generated_ict: string; received_at: string; source: string; age_minutes: number | null } | null;
  worker: { started: boolean; last_tick_at: string | null; last_ok_at: string | null; last_error: string | null; cursor: string | null };
  webhook: { failures_24h: number | null };
}

/** The newest snapshot. Pass the previous ETag to get a cheap 304. */
export const getSnapshot = (etag?: string | null): Promise<BotOpsResponse<Snapshot>> =>
  botOpsFetch<Snapshot>('/snapshot', { etag });

export const getHealth = (): Promise<BotOpsResponse<HealthData>> => botOpsFetch<HealthData>('/health');

/** One bot with its JD, every recommendation and its documents. */
export const getBot = (slug: string): Promise<BotOpsResponse<Bot>> =>
  botOpsFetch<Bot>(`/bots/${encodeURIComponent(slug)}`);

export type { SnapshotMeta };

// ---- Writes (super admin only; the server enforces it) ---------------------

export interface DecisionResult {
  decision: { action_id: string; value: string; message_id?: string | null };
  message_id: string | null;
  replayed?: boolean;
  noop?: boolean;
  webhook?: { sent: boolean; status: string };
  gchat?: { status: string; error?: string };
}

/** Yes / No / Undo (`pending`). The Idempotency-Key makes a retry safe: the same key never writes twice. */
export interface ChatRequest { post: true; stakeholders: string[]; message: string }

export const postDecision = (p: { actionId: string; value: 'yes' | 'no' | 'pending'; note?: string | null; idempotencyKey: string; gchat?: ChatRequest | null }) =>
  botOpsFetch<DecisionResult>('/decisions', {
    method: 'POST',
    body: { action_id: p.actionId, value: p.value, note: p.note ?? null, ...(p.gchat ? { gchat: p.gchat } : {}) },
    headers: { 'Idempotency-Key': p.idempotencyKey },
  });

export interface InstructionInput {
  targetBotSlug: string;
  text: string;
  kind: InstructionKind;
  relatedActionId?: string | null;
  priority?: 'P0' | 'P1' | 'P2';
}

export const postInstruction = (p: InstructionInput) =>
  botOpsFetch<{ instruction: Instruction; message_id: string; webhook?: { sent: boolean; status: string } }>('/instructions', {
    method: 'POST',
    body: { target_bot_slug: p.targetBotSlug, text: p.text, kind: p.kind, related_action_id: p.relatedActionId || null, priority: p.priority || 'P1' },
  });

export const markInstructionSeen = (id: string) =>
  botOpsFetch<Instruction>(`/instructions/${encodeURIComponent(id)}/seen`, { method: 'POST', body: {} });

export const nudgeInstruction = (id: string) =>
  botOpsFetch<{ instruction: Instruction; webhook: { sent: boolean; status: string } | null }>(`/instructions/${encodeURIComponent(id)}/nudge`, { method: 'POST', body: {} });

// ---- Sync ------------------------------------------------------------------

export interface SyncRequestRow { id: string; created_at: string; acked_at: string | null; done_at: string | null; status: string | null }
export interface SyncStatus {
  latest_message_id: string | null;
  new_messages: { id: string; thread_id: string; type: string; created_at: string }[];
  latest_snapshot: { sha256: string; generated_ict: string; received_at: string } | null;
  generated_ict: string | null;
  sync_requests: SyncRequestRow[];
}

export const requestSync = () => botOpsFetch<{ request_id: string; webhook?: { sent: boolean; status: string } }>('/sync', { method: 'POST', body: {} });
export const getSyncStatus = (after?: string | null) => botOpsFetch<SyncStatus>(`/sync${after ? `?after=${encodeURIComponent(after)}` : ''}`);

/** "failed · Retry" on the Chat chip. */
export const retryChatPost = (actionId: string) => botOpsFetch<{ gchat: { status: string } }>('/gchat/retry', { method: 'POST', body: { action_id: actionId } });

// ---- Phase 4 ------------------------------------------------------------------------

export interface BulkResult { bulk_id: string; value: string; results: { action_id: string; ok: boolean; noop?: boolean; already_in_progress?: boolean; code?: string; error?: string }[] }

/** Approve / Decline selected, and (with `pending`) its 10 s Undo. One idempotency key covers the whole request. */
export const postBulkDecisions = (p: { items: string[]; value: 'yes' | 'no' | 'pending'; idempotencyKey: string }) =>
  botOpsFetch<BulkResult>('/decisions/bulk', { method: 'POST', body: { items: p.items, value: p.value }, headers: { 'Idempotency-Key': p.idempotencyKey } });

export const getAudit = (p: { type?: string | null; before?: string | null; limit?: number } = {}) => {
  const q = new URLSearchParams();
  if (p.type) q.set('type', p.type);
  if (p.before) q.set('before', p.before);
  if (p.limit) q.set('limit', String(p.limit));
  const s = q.toString();
  return botOpsFetch<AuditRow[]>(`/audit${s ? `?${s}` : ''}`);
};

export const getActivity = () => botOpsFetch<ActivityData>('/activity');
export const getScorecard = () => botOpsFetch<Scorecard>('/scorecard');
export const getPrefs = () => botOpsFetch<Prefs>('/prefs');
export const savePrefs = (p: { theme?: 'light' | 'dark' | null; notify?: Partial<Prefs['notify']> }) => botOpsFetch<Prefs>('/prefs', { method: 'PUT', body: p });
export const markAllSeen = () => botOpsFetch<Prefs>('/prefs/seen', { method: 'POST', body: {} });
export const getViews = () => botOpsFetch<SavedView[]>('/views');
export const createView = (p: { name: string; query: string }) => botOpsFetch<SavedView>('/views', { method: 'POST', body: p });
export const deleteView = (id: string) => botOpsFetch<{ id: string }>(`/views/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const getSearch = (q: string) => botOpsFetch<import('../lib/palette').ServerHits>(`/search?q=${encodeURIComponent(q)}`);
